import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, TextField } from "@mui/material";
import { GridLoader } from "react-spinners";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { buscarLancamentosEsocial } from "../../../services/apuracao.service";
import { formatCurrency } from "../../../utils/formatValue";
import { formatarCnpj, formatarCpf } from "../utils";
import {
  competenciaParaPt,
  competenciaParaYm,
} from "./utils/competenciaYm.util";
import FiltrosDinamicosLancamentos from "../components/FiltrosDinamicosLancamentos";
import styles from "./detalheEsocial.styles.module.css";

/** Catálogo alinhado ao protótipo esocial-lacamentos (initInssLancamentos). */
const COLUNAS_UI = [
  { campo: "competencia", label: "Competência" },
  { campo: "cpf", label: "CPF", cell: "cpf" },
  { campo: "codigoRubrica", label: "Código da Rubrica", cell: "mono" },
  { campo: "descricaoRubrica", label: "Descrição" },
  { campo: "valorBase", label: "Valor Base", cell: "money", num: true },
  { campo: "valorInss", label: "Valor INSS", cell: "money", num: true },
  { campo: "valorTerceiros", label: "Terceiros", cell: "money", num: true },
  { campo: "valorRat", label: "RAT", cell: "money", num: true },
  { campo: "valorTotal", label: "Total", cell: "money", num: true },
  { campo: "valorInssSelic", label: "Valor INSS Selic", cell: "money", num: true },
  { campo: "valorTerceirosSelic", label: "Terceiros Selic", cell: "money", num: true },
  { campo: "valorRatSelic", label: "RAT Selic", cell: "money", num: true },
  { campo: "valorTotalSelic", label: "Total Selic", cell: "money", num: true },
  { campo: "aliqSelic", label: "Selic", cell: "selic", num: true },
  { campo: "valorSelic", label: "Valor Selic", cell: "money", num: true },
  { campo: "idArquivo", label: "ID arquivo", cell: "mono" },
  { campo: "dataImportacao", label: "Data de Importação", cell: "date" },
];

const LIMITE_PADRAO = 10;
const PAGE_SIZES = [10, 50, 100];

/** TXM-480 §2 — colunas que não tinham filtro de range na tela de Lançamentos. */
const CAMPOS_RANGE = [
  { campo: "valorInss", label: "Valor INSS" },
  { campo: "valorTerceiros", label: "Terceiros" },
  { campo: "valorRat", label: "RAT" },
  { campo: "valorTotal", label: "Total" },
  { campo: "valorInssSelic", label: "Valor INSS Selic" },
  { campo: "valorTerceirosSelic", label: "Terceiros Selic" },
  { campo: "valorRatSelic", label: "RAT Selic" },
  { campo: "valorTotalSelic", label: "Total Selic" },
  { campo: "aliqSelic", label: "Selic (%)" },
];

/**
 * Campos que já têm campo dedicado na tela (período, código da rúbrica, os 9 de valor) e por
 * isso ficam fora do catálogo do filtro dinâmico genérico, para não duplicar UI.
 * baseLegal/flagIncidencia/verbaFerias do Detalhe pai NÃO entram aqui de propósito: não
 * existem como coluna em relatorios.z_*_esocial (só na sumarização) — ver
 * api-relatorios/.docs/TXM-336/sql/criar_tabela_esocial.sql.
 */
const CAMPOS_EXCLUIDOS_FILTRO_DINAMICO = new Set([
  ...CAMPOS_RANGE.map((c) => c.campo),
  "competencia",
  "codigoRubrica",
]);

function filtrosRangeVazios() {
  return Object.fromEntries(CAMPOS_RANGE.map(({ campo }) => [campo, { min: "", max: "" }]));
}

function parseNumeroBr(v) {
  if (v === "" || v == null) return undefined;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : undefined;
}

/** Converte o estado {campo: {min,max}} no contrato filtrosDinamicos da API. */
function montarFiltrosDinamicos(filtrosRange) {
  return CAMPOS_RANGE.map(({ campo }) => {
    const { min, max } = filtrosRange[campo] || {};
    const valor = parseNumeroBr(min);
    const valor2 = parseNumeroBr(max);
    if (valor === undefined && valor2 === undefined) return null;
    return { campo, tipo: "range", valor, valor2 };
  }).filter(Boolean);
}

function formatarCelula(col, valor) {
  if (valor == null || valor === "") return "—";
  if (col.campo === "competencia") return competenciaParaPt(valor);
  if (col.cell === "cpf") return formatarCpf(valor);
  if (col.cell === "selic") {
    const n = Number(valor);
    return Number.isFinite(n)
      ? `${n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
      : String(valor);
  }
  if (col.cell === "money" || col.num) return formatCurrency(Number(valor) || 0);
  if (col.cell === "date") {
    const s = String(valor);
    if (s.length >= 10 && s.includes("-")) {
      const [y, m, d] = s.slice(0, 10).split("-");
      return `${d}/${m}/${y}`;
    }
    return s;
  }
  return String(valor);
}

function classeCelula(col) {
  if (col.cell === "money" || col.cell === "selic" || col.num) {
    return `${styles.num} ${styles.colValor}`;
  }
  if (col.cell === "cpf" || col.cell === "mono") return styles.rubricaCod;
  return undefined;
}

/**
 * Tela dedicada de Lançamentos eSocial (protótipo esocial-lacamentos).
 * Query: codigoRubrica (obrig.), competencia (YYYY-MM, opcional), descricao (opcional).
 * Exportação fica no menu Exportar do Detalhe (padrão TXM-336 / SPED).
 */
export default function ApuracaoDetalheLancamentosEsocial() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const codigoRubrica = String(searchParams.get("codigoRubrica") || "").trim();
  const competenciaUrl = competenciaParaYm(searchParams.get("competencia"));
  const descricaoUrl = String(searchParams.get("descricao") || "").trim();

  const seedFiltrosDinamicos = useCallback(
    () =>
      descricaoUrl
        ? [{ campo: "descricaoRubrica", tipo: "text", valor: descricaoUrl, valor2: "" }]
        : [],
    [descricaoUrl]
  );

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [dados, setDados] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(LIMITE_PADRAO);
  const [perapurInicioInput, setPerapurInicioInput] = useState(competenciaUrl);
  const [perapurFimInput, setPerapurFimInput] = useState(competenciaUrl);
  const [perapurInicioAplicado, setPerapurInicioAplicado] = useState(competenciaUrl);
  const [perapurFimAplicado, setPerapurFimAplicado] = useState(competenciaUrl);
  const [filtrosRangeInput, setFiltrosRangeInput] = useState(filtrosRangeVazios());
  const [filtrosRangeAplicados, setFiltrosRangeAplicados] = useState(filtrosRangeVazios());
  const [filtrosDinamicos, setFiltrosDinamicos] = useState(seedFiltrosDinamicos);
  const [filtrosDinamicosAplicados, setFiltrosDinamicosAplicados] = useState(seedFiltrosDinamicos);
  const [filtrosExpandido, setFiltrosExpandido] = useState(false);

  useEffect(() => {
    setPerapurInicioInput(competenciaUrl);
    setPerapurFimInput(competenciaUrl);
    setPerapurInicioAplicado(competenciaUrl);
    setPerapurFimAplicado(competenciaUrl);
    setPagina(1);
    setFiltrosRangeInput(filtrosRangeVazios());
    setFiltrosRangeAplicados(filtrosRangeVazios());
    const seed = seedFiltrosDinamicos();
    setFiltrosDinamicos(seed);
    setFiltrosDinamicosAplicados(seed);
  }, [competenciaUrl, codigoRubrica, seedFiltrosDinamicos]);

  const setFiltroRange = (campo, lado, valor) => {
    setFiltrosRangeInput((prev) => ({ ...prev, [campo]: { ...prev[campo], [lado]: valor } }));
  };

  const handleFiltrar = () => {
    setPerapurInicioAplicado(perapurInicioInput);
    setPerapurFimAplicado(perapurFimInput);
    setFiltrosRangeAplicados(filtrosRangeInput);
    setFiltrosDinamicosAplicados(filtrosDinamicos);
    setPagina(1);
  };

  const handleLimparFiltros = () => {
    const vazio = filtrosRangeVazios();
    setFiltrosRangeInput(vazio);
    setFiltrosRangeAplicados(vazio);
    setFiltrosDinamicos([]);
    setFiltrosDinamicosAplicados([]);
    setPagina(1);
  };

  const colunasFiltroDinamico = useMemo(
    () =>
      (dados?.colunas || [])
        .filter((c) => !CAMPOS_EXCLUIDOS_FILTRO_DINAMICO.has(c.campo))
        .map((c) => ({ id: c.campo, label: c.label, filter: c.filter })),
    [dados]
  );

  const carregar = useCallback(async () => {
    if (!codigoRubrica) {
      setLoading(false);
      setErro("Código da rúbrica não informado.");
      setDados(null);
      return;
    }

    setLoading(true);
    setErro("");

    const ini = competenciaParaYm(perapurInicioAplicado);
    const fim = competenciaParaYm(perapurFimAplicado);
    const filtrosDinamicosPayload = [
      ...montarFiltrosDinamicos(filtrosRangeAplicados),
      ...filtrosDinamicosAplicados
        .map((f) => ({
          ...f,
          valor: String(f.valor ?? "").trim(),
          valor2: String(f.valor2 ?? "").trim(),
        }))
        .filter((f) => f.valor || f.valor2),
    ];

    const res = await buscarLancamentosEsocial(id, {
      codigoRubrica,
      pagina,
      limite,
      ...(ini ? { perapurInicio: ini } : {}),
      ...(fim ? { perapurFim: fim } : {}),
      ...(filtrosDinamicosPayload.length ? { filtrosDinamicos: filtrosDinamicosPayload } : {}),
    });

    setLoading(false);

    if (!res.sucesso) {
      setErro(res.mensagem || "Não foi possível carregar os lançamentos.");
      setDados(null);
      if (res.status === 403) {
        navigate("/relatorios/apuracoes", { replace: true });
      }
      return;
    }

    setDados(res);
  }, [
    codigoRubrica,
    id,
    limite,
    navigate,
    pagina,
    perapurFimAplicado,
    perapurInicioAplicado,
    filtrosRangeAplicados,
    filtrosDinamicosAplicados,
  ]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const totalPaginas = dados?.paginacao?.totalPaginas || 1;
  const total = dados?.paginacao?.total ?? 0;
  const paginaAtual = dados?.paginacao?.pagina || pagina;
  const limiteAtual = dados?.paginacao?.limite || limite;
  const start = total > 0 ? (paginaAtual - 1) * limiteAtual + 1 : 0;
  const end = total > 0 ? Math.min(paginaAtual * limiteAtual, total) : 0;

  const ap = dados?.apuracao || {};
  const rubricaApi = dados?.rubrica || {};
  const descricaoRubrica = rubricaApi.descricaoRubrica || descricaoUrl || "";

  const refConta = [
    competenciaUrl ? competenciaParaPt(competenciaUrl) : null,
    codigoRubrica || null,
  ]
    .filter(Boolean)
    .join(" · ");

  if (!codigoRubrica && !loading) {
    return (
      <div className={styles.pageWrap}>
        <div className={styles.container}>
          <Alert severity="warning">
            Informe a rúbrica na URL (<code>codigoRubrica</code>) ou use o botão Detalhes na tela de
            Detalhe.
          </Alert>
          <button
            type="button"
            className={styles.linkVoltar}
            style={{ marginTop: 16 }}
            onClick={() => navigate(`/relatorios/apuracoes/${id}/detalhe`)}
          >
            ← Voltar aos detalhes da apuração
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrap}>
      <div className={styles.container}>
        <button
          type="button"
          className={styles.linkVoltar}
          onClick={() => navigate(`/relatorios/apuracoes/${id}/detalhe`)}
        >
          ← Voltar aos detalhes da apuração
        </button>

        <header className={styles.pageHeader}>
          <div className={styles.headerMain}>
            <h1 className={styles.pageTitle}>Lançamentos — eSocial</h1>
            <p className={`${styles.pageSubtitle} ${styles.lancamentosSubtitle}`}>
              {ap.empresa || "—"} · CNPJ {formatarCnpj(ap.cnpj)} · Apuração #{id} · eSocial
              {refConta ? (
                <>
                  {" "}
                  · Ref.: <span className={styles.contaRef}>{refConta}</span>
                  {descricaoRubrica ? ` ${descricaoRubrica}` : ""}
                </>
              ) : null}
              {rubricaApi.baseLegalId != null ? (
                <span className={styles.badgeBaseLegal}>{rubricaApi.baseLegalDescricao}</span>
              ) : null}
            </p>
          </div>
        </header>

        <div className={styles.lancamentosToolbar}>
          <TextField
            label="Competência início"
            type="month"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={perapurInicioInput || ""}
            onChange={(e) => setPerapurInicioInput(competenciaParaYm(e.target.value))}
          />
          <TextField
            label="Competência fim"
            type="month"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={perapurFimInput || ""}
            onChange={(e) => setPerapurFimInput(competenciaParaYm(e.target.value))}
          />
          <button type="button" className={styles.btnPage} onClick={handleFiltrar}>
            Filtrar
          </button>
          <button type="button" className={styles.btnLimpar} onClick={handleLimparFiltros}>
            Limpar filtros
          </button>
        </div>

        <div
          className={`${styles.filtersSection} ${
            !filtrosExpandido ? styles.filtersSectionCollapsed : ""
          }`}
        >
          <button
            type="button"
            className={`${styles.filtersHeader} ${
              filtrosExpandido ? styles.filtersHeaderExpanded : ""
            }`}
            onClick={() => setFiltrosExpandido((v) => !v)}
            aria-expanded={filtrosExpandido}
            aria-controls="filtros-lancamentos-esocial-body"
          >
            <span className={styles.filtersTitle}>Filtros de valores e outras colunas</span>
            <span
              className={`${styles.filtersToggleIcon} ${
                filtrosExpandido ? styles.filtersToggleIconOpen : ""
              }`}
              aria-hidden
            >
              ▼
            </span>
          </button>

          {filtrosExpandido && (
            <div id="filtros-lancamentos-esocial-body" className={styles.filtersBody}>
              <div className={styles.filtersRow}>
                {CAMPOS_RANGE.map(({ campo, label }) => (
                  <div key={campo} className={styles.filterGroup}>
                    <span className={styles.filterLabel}>{label}</span>
                    <div className={styles.filterTwin}>
                      <input
                        className={styles.filterInput}
                        type="text"
                        inputMode="decimal"
                        placeholder="Mín."
                        value={filtrosRangeInput[campo]?.min ?? ""}
                        onChange={(e) => setFiltroRange(campo, "min", e.target.value)}
                        aria-label={`${label} — mínimo`}
                      />
                      <span className={styles.filterTwinSep}>até</span>
                      <input
                        className={styles.filterInput}
                        type="text"
                        inputMode="decimal"
                        placeholder="Máx."
                        value={filtrosRangeInput[campo]?.max ?? ""}
                        onChange={(e) => setFiltroRange(campo, "max", e.target.value)}
                        aria-label={`${label} — máximo`}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {colunasFiltroDinamico.length > 0 && (
                <FiltrosDinamicosLancamentos
                  colunas={colunasFiltroDinamico}
                  filtros={filtrosDinamicos}
                  onChange={setFiltrosDinamicos}
                />
              )}
            </div>
          )}
        </div>

        {erro && !dados ? (
          <Alert severity="error">{erro}</Alert>
        ) : loading && !dados ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <GridLoader color="#18316e" />
          </Box>
        ) : (
          <>
            {erro ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {erro}
              </Alert>
            ) : null}
            <div className={`${styles.tableWrap} ${styles.lancamentosTableWrap}`}>
              <table className={`${styles.table} ${styles.lancamentosTable}`}>
                <thead>
                  <tr>
                    {COLUNAS_UI.map((c) => (
                      <th
                        key={c.campo}
                        className={
                          c.cell === "money" || c.cell === "selic" || c.num ? styles.num : undefined
                        }
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(dados?.linhas || []).map((l, i) => (
                    <tr
                      key={`${l.idOrigem ?? i}-${l.competencia ?? ""}-${l.cpf ?? ""}`}
                      className={rubricaApi.baseLegalId != null ? styles.linhaComBaseLegal : undefined}
                    >
                      {COLUNAS_UI.map((c) => (
                        <td key={c.campo} className={classeCelula(c)}>
                          {formatarCelula(c, l[c.campo])}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {(dados?.linhas || []).length === 0 && (
                    <tr>
                      <td colSpan={COLUNAS_UI.length} className={styles.empty}>
                        Nenhum lançamento encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {total > 0 ? (
              <div className={styles.paginationBar}>
                <span className={styles.paginationInfo}>
                  Mostrando {start}–{end} de {total.toLocaleString("pt-BR")} lançamento
                  {total !== 1 ? "s" : ""} · página {paginaAtual} de {Math.max(1, totalPaginas)}
                  {loading ? " · atualizando…" : ""}
                </span>
                <div className={styles.paginationControls}>
                  <div className={styles.pgSizeGroup}>
                    <label htmlFor="eso-lanc-pg-size" className={styles.pgSizeLabel}>
                      Lançamentos por página
                    </label>
                    <select
                      id="eso-lanc-pg-size"
                      className={styles.pageSizeSelect}
                      value={limite}
                      aria-label="Lançamentos por página"
                      onChange={(e) => {
                        setLimite(Number(e.target.value));
                        setPagina(1);
                      }}
                    >
                      {PAGE_SIZES.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    className={styles.btnPage}
                    disabled={pagina <= 1 || loading}
                    onClick={() => setPagina((p) => Math.max(1, p - 1))}
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    className={styles.btnPage}
                    disabled={pagina >= totalPaginas || loading}
                    onClick={() => setPagina((p) => p + 1)}
                  >
                    Próxima
                  </button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
